/**
 * SupplyChain - Explicação da Cadeia Produtiva da Borracha
 * Fonte: Estudo CNA/FAESP sobre Mercado de Borracha Natural
 */

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useTranslation } from '../../translations';

const SupplyChain = () => {
  const { t } = useTranslation();

  const chainSteps = [
    {
      id: 1,
      title: t('tapperHeveicultor'),
      emoji: '🌳',
      color: 'bg-green-100 border-green-300',
      textColor: 'text-green-800',
      description: t('tapperDescription'),
      product: t('tapperProduct'),
      details: [
        t('tapperDetail1'),
        t('tapperDetail2'),
        t('tapperDetail3'),
        t('tapperDetail4'),
      ],
      priceRef: t('coagulumPriceRef'),
    },
    {
      id: 2,
      title: t('processorMill'),
      emoji: '🏭',
      color: 'bg-blue-100 border-blue-300',
      textColor: 'text-blue-800',
      description: t('processorDescription'),
      product: t('processorProduct'),
      details: [
        t('processorDetail1'),
        t('processorDetail2'),
        t('processorDetail3'),
        t('processorDetail4'),
      ],
      priceRef: t('geb10PriceRef'),
    },
    {
      id: 3,
      title: t('tireIndustry'),
      emoji: '🛞',
      color: 'bg-orange-100 border-orange-300',
      textColor: 'text-orange-800',
      description: t('tireIndustryDescription'),
      product: t('tireIndustryProduct'),
      details: [
        t('tireIndustryDetail1'),
        t('tireIndustryDetail2'),
        t('tireIndustryDetail3'),
        t('tireIndustryDetail4'),
      ],
      priceRef: t('industrialPriceRef'),
    },
    {
      id: 4,
      title: t('finalConsumer'),
      emoji: '🚗',
      color: 'bg-purple-100 border-purple-300',
      textColor: 'text-purple-800',
      description: t('finalConsumerDescription'),
      product: t('finalConsumerProduct'),
      details: [
        t('finalConsumerDetail1'),
        t('finalConsumerDetail2'),
        t('finalConsumerDetail3'),
      ],
      priceRef: t('consumerPriceRef'),
    },
  ];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800 flex items-center gap-2">
            🔗 {t('supplyChainTitle')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600 mb-6">
            {t('supplyChainDescription')}
          </p>

          {/* Visual Chain */}
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-green-200 -translate-y-1/2 z-0" />
            
            {/* Steps */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 relative z-10">
              {chainSteps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center">
                  {/* Arrow for mobile */}
                  {index > 0 && (
                    <div className="md:hidden text-2xl text-green-500 my-2">↓</div>
                  )}
                  
                  {/* Card */}
                  <div className={`w-full p-4 rounded-lg border-2 ${step.color} text-center`}>
                    <div className="text-4xl mb-2">{step.emoji}</div>
                    <h3 className={`font-bold ${step.textColor}`}>{step.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{step.description}</p>
                    <div className="mt-2 bg-white rounded px-2 py-1">
                      <span className="text-xs font-medium text-gray-700">{step.product}</span>
                    </div>
                  </div>
                  
                  {/* Arrow for desktop */}
                  {index < chainSteps.length - 1 && (
                    <div className="hidden md:block absolute text-2xl text-green-500" 
                         style={{ left: `${(index + 1) * 25 - 2}%`, top: '50%', transform: 'translateY(-50%)' }}>
                      →
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Steps */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800">📋 {t('detailsOfEachStep')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {chainSteps.map((step) => (
              <div key={step.id} className={`p-4 rounded-lg border ${step.color}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">{step.emoji}</span>
                  <h4 className={`font-bold ${step.textColor}`}>{step.title}</h4>
                </div>
                <ul className="space-y-1">
                  {step.details.map((detail, idx) => (
                    <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-green-500">•</span>
                      {detail}
                    </li>
                  ))}
                </ul>
                <div className="mt-3 pt-2 border-t border-gray-200">
                  <span className="text-xs text-gray-500">{t('referenceLabel')}: {step.priceRef}</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Cooperativas e Importações */}
      <Card className="border-2 border-amber-400">
        <CardHeader>
          <CardTitle className="text-amber-800">Atores Importantes na Cadeia</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🤝</span>
                <h4 className="font-bold text-green-800">Cooperativas</h4>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                Cooperativas fortalecem o poder de barganha do produtor, negociando em nome de vários associados.
              </p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-green-500">&#10003;</span>Negociação coletiva = taxas melhores (70-85%)</li>
                <li className="flex items-start gap-2"><span className="text-green-500">&#10003;</span>Regularidade de volume = estabilidade</li>
                <li className="flex items-start gap-2"><span className="text-green-500">&#10003;</span>Assistência técnica compartilhada</li>
              </ul>
            </div>
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-2xl">🚢</span>
                <h4 className="font-bold text-red-800">Importações</h4>
              </div>
              <p className="text-sm text-gray-700 mb-2">
                Brasil importa 40-50% da borracha que consome. Importação barata pressiona o preço do produtor nacional.
              </p>
              <ul className="space-y-1 text-sm text-gray-600">
                <li className="flex items-start gap-2"><span className="text-red-500">&#10007;</span>Alíquota de importação: ~3,2% (FAESP pede 22%)</li>
                <li className="flex items-start gap-2"><span className="text-red-500">&#10007;</span>Indústria prioriza importado quando mais barato</li>
                <li className="flex items-start gap-2"><span className="text-red-500">&#10007;</span>Concorrência direta com Ásia (custo menor)</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Key Facts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-green-800">📊 {t('importantData')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-green-700">~70%</div>
              <p className="text-sm text-gray-600">{t('nationalProductionConsumed')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('sourceLabel')}: CNA/FAESP</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-blue-700">São Paulo</div>
              <p className="text-sm text-gray-600">{t('saoPauloConcentrates')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('sourceLabel')}: IEA/SAA-SP</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <div className="text-3xl font-bold text-orange-700">~50%</div>
              <p className="text-sm text-gray-600">{t('domesticConsumptionImported')}</p>
              <p className="text-xs text-gray-400 mt-1">{t('sourceLabel')}: CONAB</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupplyChain;
